import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock, Download, CreditCard, LogOut } from "lucide-react";
import { CycleToggle } from "@/components/billing/PlanPicker";
import { euro } from "@/lib/revolutPay";


export default function TrialGate() {
  const { user, org, logout, refreshOrg } = useAuth();
  const { theme } = useTheme();
  const brandLogo = theme === "dark" ? "/Logo_Full_White.webp" : "/Logo_Full_Black.webp";
  const [rev, setRev] = useState(null);
  const [busy, setBusy] = useState("");
  const [offer, setOffer] = useState(null);
  const [cycle, setCycle] = useState("monthly");

  const b = org?.billing || {};
  const status = b.subscription_status || "none";
  const trialEnd = b.trial_end ? new Date(b.trial_end).getTime() : null;
  const trialExpired = status === "trial" && trialEnd && trialEnd < Date.now();
  const hasActive = status === "active";
  const hasValidTrial = status === "trial" && trialEnd && trialEnd > Date.now();
  // Block whenever there is neither an active subscription nor a valid trial.
  const blocked = !!org && !user?.is_platform_admin && !hasActive && !hasValidTrial;

  useEffect(() => {
    if (blocked) {
      api.get("/revolut/config").then(({ data }) => setRev(data)).catch(() => {});
      api.get("/billing/offer").then(({ data }) => { setOffer(data); setCycle(data.current.cycle || "monthly"); }).catch(() => {});
    }
  }, [blocked]);

  if (!blocked) return null;

  const plan = (b.plan || "").toLowerCase();
  const planInfo = offer?.plans.find((p) => p.key === plan);
  const price = planInfo ? (cycle === "yearly" ? planInfo.price_yearly : planInfo.price_monthly) : null;

  const pay = async () => {
    if (!plan) { toast.error("Aucune formule sélectionnée. Contactez le support."); return; }
    if (rev && !rev.configured) { toast.error("Le paiement en ligne est momentanément indisponible, réessayez plus tard."); return; }
    if (!rev?.plans?.[plan] || (cycle === "yearly" && !rev.plans[plan].yearly)) { toast.error("Ce plan n'est pas encore configuré. Contactez le support."); return; }
    setBusy("pay");
    try {
      const { data } = await api.post("/revolut/subscribe", { plan, cycle });
      if (!data.token) throw new Error("Aucun jeton de paiement reçu");
      const mod = await import("@revolut/checkout");
      const RC = mod.default || mod.RevolutCheckout;
      const rc = await RC(data.token, data.mode === "production" ? "prod" : "sandbox");
      rc.payWithPopup({
        savePaymentMethodFor: "merchant",
        onSuccess: async () => {
          toast.success("Abonnement activé — merci !");
          try { const me = await api.get("/auth/me"); refreshOrg(me.data.org); } catch { /* noop */ }
        },
        onError: () => toast.error("Le paiement Revolut a échoué."),
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Erreur");
    } finally { setBusy(""); }
  };

  const exportData = async () => {
    setBusy("export");
    try {
      const res = await api.get("/account/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "donnees-halgaros.zip";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Téléchargement lancé");
    } catch { toast.error("Échec du téléchargement"); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="trial-gate" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md">
      <div className="w-full max-w-md rounded-3xl border border-zinc-800 bg-zinc-950 p-8 text-center shadow-2xl">
        <img src={brandLogo} alt="HalgarOS" className="mx-auto mb-5 h-9 w-auto" />
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FF6A00]/15"><Lock className="h-7 w-7 text-[#FF6A00]" /></div>
        <h2 className="mt-4 font-display text-2xl font-bold text-white">{trialExpired ? "Votre essai est terminé" : "Abonnement requis"}</h2>
        <p className="mt-2 text-sm text-zinc-400">Pour continuer à utiliser HalgarOS, activez votre abonnement. Sans paiement, votre compte est mis en pause pendant 3 mois. Vos données restent conservées 1 an et vous pouvez les télécharger à tout moment.</p>

        {plan ? (
          <div className="mt-5 rounded-2xl border border-[#FF6A00]/30 bg-[#FF6A00]/10 p-4">
            <p className="text-xs uppercase tracking-wide text-[#FF6A00]">Votre formule</p>
            <p className="mt-1 font-display text-xl font-bold text-white" data-testid="trial-gate-price">{planInfo?.name || plan} · {price != null ? euro(price) : "…"}<span className="text-sm font-normal text-zinc-400"> HT /{cycle === "yearly" ? "an" : "mois"}</span></p>
            <div className="mt-3 flex justify-center"><CycleToggle cycle={cycle} setCycle={setCycle} testid="trial-gate-cycle" /></div>
          </div>
        ) : (
          <p className="mt-5 text-sm text-amber-400">Aucune formule sélectionnée — contactez le support.</p>
        )}

        <div className="mt-6 space-y-2">
          <Button data-testid="trial-gate-pay" onClick={pay} disabled={busy === "pay"} className="w-full rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><CreditCard className="mr-1.5 h-4 w-4" />Payer mon abonnement</Button>
          <Button data-testid="trial-gate-export" onClick={exportData} disabled={busy === "export"} variant="outline" className="w-full rounded-full border-zinc-700 text-zinc-200 hover:bg-zinc-800"><Download className="mr-1.5 h-4 w-4" />Récupérer mes documents</Button>
          <button data-testid="trial-gate-logout" onClick={logout} className="mx-auto mt-1 flex items-center justify-center gap-1.5 text-xs text-zinc-500 hover:text-red-400"><LogOut className="h-3.5 w-3.5" />Se déconnecter</button>
        </div>
      </div>
    </div>
  );
}
